import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToolCard } from '@/components/tools/ToolCard';
import { CompactToolCard } from '@/components/tools/CompactToolCard';
import { reachGoal } from '@/utils/analytics';
import { projectsApi, toolsApi, type RecipeView, type ToolCard as ToolCardData } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';

const NONE = '__none__';
const RECENT_KEY = 'tools.recent';

function readRecent(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    // A stored value that isn't an array of strings (corrupted write, another app
    // version's shape, manual localStorage tampering) must not reach `recent.length`/
    // `.filter` downstream — those throw on anything but a real array.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
}
function pushRecent(task: string): string[] {
  const next = [task, ...readRecent().filter((t) => t !== task)].slice(0, 5);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  return next;
}

/** First tool of every recipe path — the cards that get a «почему вам» line (≤5, the /explain cap). */
function firstOfPathSlugs(recipe: RecipeView): string[] {
  const slugs = recipe.paths
    .map((path) => path.roles.find((role) => role.tools.length > 0)?.tools[0]?.slug)
    .filter((slug): slug is string => typeof slug === 'string');
  // The same tool can lead two paths (e.g. REG.RU as hosting and as domain) — dedupe so
  // it doesn't burn two of the five /explain slots.
  return [...new Set(slugs)].slice(0, 5);
}

export default function ToolsPage() {
  const { t } = useTranslation('tools');
  const [task, setTask] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState(NONE);
  const [onlyRu, setOnlyRu] = useState(true);
  const [examples, setExamples] = useState<string[]>([]);
  const [recent, setRecent] = useState<string[]>(readRecent);
  const [results, setResults] = useState<ToolCardData[] | null>(null);
  const [recipe, setRecipe] = useState<RecipeView | null>(null);
  const [why, setWhy] = useState<Record<string, string>>({});
  const [whyLoading, setWhyLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  // The last query the user actually searched for — a filter change below re-runs it.
  const lastQuery = useRef('');

  useEffect(() => {
    projectsApi.list().then(setProjects).catch(() => setProjects([]));
    toolsApi.examples().then(setExamples).catch(() => setExamples([]));
  }, []);

  const run = useCallback(async (value: string) => {
    const query = value.trim();
    if (query.length < 3) return;
    const id = ++requestId.current;
    const pid = projectId === NONE ? undefined : projectId;
    lastQuery.current = query;
    setTask(query);
    setLoading(true);
    setError('');
    setWhy({});
    setRecipe(null);
    // A new search must not inherit a stuck why-pulse from a still-in-flight previous
    // one: if search A was explainable and is still awaiting explain(A) when search B
    // starts, B bumps requestId so A's own `finally` correctly no-ops on it — but
    // nothing else would ever flip whyLoading back to false for B if it isn't reset here
    // (e.g. B turns out not to be explainable and skips the explain block entirely).
    setWhyLoading(false);

    let response: { results: ToolCardData[]; explainable: boolean; recipe: RecipeView | null };
    try {
      response = await toolsApi.search({ task: query, projectId: pid, onlyRuAccessible: onlyRu });
    } catch (e) {
      if (id === requestId.current) {
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
      return;
    }
    if (id !== requestId.current) return;

    setResults(response.results);
    setRecipe(response.recipe);
    setRecent(pushRecent(query));
    reachGoal('tools_search');
    // Cards render as soon as results are in — `loading` must drop here rather than
    // after the (separate, best-effort) explain call below: otherwise the cards stay
    // hidden behind the skeleton for as long as the explain LLM call takes, and the
    // per-card why-pulse (which only shows once results are rendered) could never show.
    setLoading(false);

    // The backend only returns `explainable: true` when the search request carried an
    // owned projectId — without a project there is nothing for it to explain, so `pid`
    // gates the call here too, matching that backend rule. In recipe mode the line goes
    // to the first card of each path instead of the flat list.
    const explainSlugs = response.recipe ? firstOfPathSlugs(response.recipe) : response.results.map((r) => r.slug);
    if (response.explainable && pid && explainSlugs.length > 0) {
      setWhyLoading(true);
      try {
        const lines = await toolsApi.explain({ task: query, projectId: pid, slugs: explainSlugs });
        if (id === requestId.current) setWhy(lines);
      } catch {
        // A failed explain call must not take already-rendered results down with it —
        // the cards simply show without "why" lines, no page-level error.
      } finally {
        if (id === requestId.current) setWhyLoading(false);
      }
    }
  }, [projectId, onlyRu]);

  // F-08.1 (стенд): смена галочки «Только доступные в России» или проекта раньше
  // оставляла на экране результаты старого поиска (так Replit «Не работает из РФ»
  // оставался в выдаче при включённой галочке) — перезапускаем последний запрос.
  // `run` пересоздаётся ровно при смене projectId/onlyRu, так что зависимость [run]
  // срабатывает на те же два события; на монте lastQuery пуст и поиска не будет.
  useEffect(() => {
    if (lastQuery.current) void run(lastQuery.current);
  }, [run]);

  const go = (slug: string) => {
    reachGoal('tools_click');
    toolsApi.go(slug).catch((e) => toast.error(e instanceof Error ? e.message : String(e)));
  };

  const chips = recent.length ? recent : examples;

  return (
    <div className="flex flex-col gap-[24px]">
      <PageHeader title={t('title')} description={t('subtitle')} />

      <input
        value={task}
        onChange={(e) => setTask(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void run(task); }}
        placeholder={t('input.placeholder')}
        aria-label={t('input.placeholder')}
        maxLength={300}
        className="h-16 w-full rounded-[24px] border border-[var(--grey-accent,#eeeeee)] bg-white px-6 font-segoe text-[22px] text-[color:var(--black-text,#0d0d0d)] shadow-sm outline-none focus:border-[color:var(--main-brand,#2197f1)]"
      />

      {chips.length > 0 && (
        <div className="flex flex-col gap-2">
          {/* M-6: label the row only when it's actually showing recent searches — the
              examples fallback (used when there's no history yet) needs no label. */}
          {recent.length > 0 && (
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">{t('recent')}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {chips.map((chip) => (
              <button key={chip} type="button" onClick={() => void run(chip)}
                className="rounded-full border border-[var(--grey-accent,#eeeeee)] bg-white px-3 py-1.5 font-segoe text-[14px] text-[color:var(--grey-text,#565858)] hover:border-[color:var(--main-brand,#2197f1)]">
                {chip}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="w-full max-w-[320px]">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger aria-label={t('project.label')}><SelectValue placeholder={t('project.none')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('project.none')}</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 font-segoe text-[14px] text-[color:var(--black-text,#0d0d0d)]">
          <input type="checkbox" checked={onlyRu} onChange={(e) => setOnlyRu(e.target.checked)}
            className="h-5 w-5 rounded accent-[color:var(--main-brand,#2197f1)]" /> {t('onlyRu')}
        </label>
      </div>

      {error && <p role="alert" className="font-segoe text-[15px] text-[color:var(--error-strong,#b3261e)]">{error}</p>}

      {loading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => <div key={i} className="h-[260px] animate-pulse rounded-[24px] bg-white" />)}
        </div>
      )}

      {!loading && recipe && (
        <section aria-label={recipe.title} className="flex flex-col gap-6">
          <h2 className="font-manrope text-[22px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
            {t('recipe.title', { recipe: recipe.title })}
          </h2>

          {recipe.paths.map((path) => {
            const firstRoleIndex = path.roles.findIndex((role) => role.tools.length > 0);
            return (
              <div key={path.id} className="flex flex-col gap-4">
                <div className="flex flex-col gap-0.5">
                  <h3 className="font-manrope text-[17px] font-semibold text-[color:var(--black-text,#0d0d0d)]">{path.title}</h3>
                  <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">{path.subtitle}</p>
                </div>

                {path.roles.map((role, roleIndex) => (
                  <div key={role.id} className="flex flex-col gap-2">
                    <div>
                      <h4 className="font-segoe text-[15px] font-semibold text-[color:var(--black-text,#0d0d0d)]">{role.title}</h4>
                      <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">{role.hint}</p>
                    </div>

                    {role.tools.length > 0 && (
                      <div className="flex gap-3 overflow-x-auto pb-2" tabIndex={0} aria-label={role.title}>
                        {role.tools.map((tool, index) => (
                          <CompactToolCard
                            key={tool.slug}
                            tool={tool}
                            why={roleIndex === firstRoleIndex && index === 0 ? why[tool.slug] : undefined}
                            whyLoading={roleIndex === firstRoleIndex && index === 0 && whyLoading && !why[tool.slug]}
                            onGo={go}
                          />
                        ))}
                      </div>
                    )}

                    {role.hiddenByFilter > 0 && (
                      <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
                        {t('recipe.hidden', { count: role.hiddenByFilter })}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </section>
      )}

      {!loading && results?.length === 0 && !recipe && (
        <p className="font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">{t('empty')}</p>
      )}

      {!loading && results && results.length > 0 && (
        <div className="flex flex-col gap-3">
          {recipe && (
            <h3 className="font-manrope text-[17px] font-semibold text-[color:var(--black-text,#0d0d0d)]">{t('recipe.more')}</h3>
          )}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {results.map((tool) => (
              <ToolCard key={tool.slug} tool={tool} why={why[tool.slug]} whyLoading={whyLoading && !why[tool.slug]} onGo={go} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
